import * as THREE from 'three';
import { BasketballDimensions } from '@shared/constants/BasketballDimensions';
import { BallSchema, BallAnimation } from '@server/rooms/schema/BasketballRoomState';

export class Ball {

  private mesh: THREE.Mesh;
  private position: THREE.Vector3 = new THREE.Vector3(0, 1, 0);
  private rotation: THREE.Euler = new THREE.Euler(0, 0, 0);
  private velocity: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private radius: number = BasketballDimensions.ball.radius;
  // Make animation public for optimized checks without getter
  public currentAnimation: string = BallAnimation.DRIBBLE;
  private dribblePhase: number = 0;
  private dribbleSpeed: number = 8; // Controls the speed of the dribble cycle
  
  // Reusable objects to avoid unnecessary allocations
  private _tempQuaternion: THREE.Quaternion = new THREE.Quaternion();
  private _tempVec3: THREE.Vector3 = new THREE.Vector3();
  
  constructor() {
    // Create ball geometry
    // double the size of a standard basketball
    const ballGeometry = new THREE.SphereGeometry(this.radius, 32, 32); // Standard basketball is about 24cm diameter
    
    // Create basketball texture with orange color and black lines
    const ballTexture = new THREE.TextureLoader().load('/images/basketball-texture.png');
    
    // Create ball material
    const ballMaterial = new THREE.MeshStandardMaterial({
      map: ballTexture,
      roughness: 0.8,
      metalness: 0.1,
      color: 0xf57c00 // Orange color for basketball
    });
    
    // Create ball mesh
    this.mesh = new THREE.Mesh(ballGeometry, ballMaterial);
    this.mesh.castShadow = true;
    this.mesh.position.copy(this.position);
    
    // Add rotation to the ball for realism

    this.rotation.set(
      Math.random() * Math.PI,
      Math.random() * Math.PI,
      Math.random() * Math.PI
    );
    this.mesh.rotation.copy(this.rotation);
  }
  
  /**
   * Update the ball's position and rotation based on server data
   * @param serverBall Ball data from the server
   */
  public updateFromServer(serverBall: BallSchema): void {
    // Add null check for dynamicBody
    if (!serverBall.dynamicBody) {
      console.warn("Ball.updateFromServer called with missing dynamicBody");
      return;
    }

    // Check and handle animation
    if (serverBall.dynamicBody.animation === BallAnimation.DRIBBLE) {
      this.currentAnimation = BallAnimation.DRIBBLE;
    } else {
      this.currentAnimation = BallAnimation.IDLE;
      
      const dynamicBody = serverBall.dynamicBody;
      const position = dynamicBody.position;
      const rotation = dynamicBody.rotation;
      const velocity = dynamicBody.velocity;
      
      // Update position from server using dynamicBody (only when not dribbling)
      // Direct assignment is faster than .set() for simple vectors
      this.position.x = position.x;
      this.position.y = position.y;
      this.position.z = position.z;
      
      // Apply position directly to mesh to avoid copy operation
      this.mesh.position.x = position.x;
      this.mesh.position.y = position.y;
      this.mesh.position.z = position.z;

      // Update rotation from server using dynamicBody - reuse quaternion object
      this._tempQuaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
      this.rotation.setFromQuaternion(this._tempQuaternion);
      
      // Apply rotation directly to mesh to avoid copy operation
      this.mesh.rotation.x = this.rotation.x;
      this.mesh.rotation.y = this.rotation.y;
      this.mesh.rotation.z = this.rotation.z;

      // Update velocity from server using dynamicBody - direct assignment
      this.velocity.x = velocity.x;
      this.velocity.y = velocity.y;
      this.velocity.z = velocity.z;
    }
  }
  
  /**
   * Update the ball animation (should be called every frame)
   * @param deltaTime Time elapsed since last update
   */
  public update(deltaTime: number): void {
    if (this.currentAnimation === BallAnimation.DRIBBLE) {
      this.updateDribbleAnimation(deltaTime);
    } else {
      // For non-dribble animations, ensure mesh rotation matches the physics rotation
      this.mesh.rotation.copy(this.rotation);
    }
  }
  
  // Pre-calculate constants for dribble animation
  private readonly DRIBBLE_MIN_Y = this.radius; // Bottom position (ball touches the ground)
  private readonly DRIBBLE_MAX_Y = 1 - this.radius; // Top position (top of ball at y=1)
  private readonly DRIBBLE_RANGE = this.DRIBBLE_MAX_Y - this.DRIBBLE_MIN_Y;
  private readonly DRIBBLE_FAST_ROTATION = 15; // Rotation speed when ball hits ground
  private readonly DRIBBLE_NORMAL_ROTATION = 5; // Normal rotation speed
  private readonly DRIBBLE_BOUNCE_THRESHOLD = 0.1; // Threshold for bounce detection
  
  /**
   * Update the dribble animation - optimized for performance
   * @param deltaTime Time elapsed since last update
   */
  private updateDribbleAnimation(deltaTime: number): void {
    // Update dribble phase using pre-calculated speed
    this.dribblePhase += deltaTime * this.dribbleSpeed;
    
    // Use absolute sine wave to get a bouncing effect (faster than multiple Math.sin calls)
    const phaseValue = this.dribblePhase % (Math.PI * 2); // Keep phase in reasonable range
    const sineValue = Math.sin(phaseValue);
    const normalizedHeight = sineValue < 0 ? -sineValue : sineValue; // Absolute value is faster than Math.abs
    
    // Calculate new Y position using pre-calculated constants
    const newY = this.DRIBBLE_MIN_Y + (normalizedHeight * this.DRIBBLE_RANGE);
    
    // Update both the position vector and mesh directly (no setY call)
    this.position.y = newY;
    this.mesh.position.y = newY;
    
    // Determine rotation speed based on normalized height
    // Use ternary operator for better performance than if/else
    const rotationSpeed = normalizedHeight < this.DRIBBLE_BOUNCE_THRESHOLD 
      ? this.DRIBBLE_FAST_ROTATION 
      : this.DRIBBLE_NORMAL_ROTATION;
    
    // Apply rotation directly
    this.mesh.rotation.x += deltaTime * rotationSpeed;
  }
  
  /**
   * Get the ball's diameter
   */
  public getDiameter(): number {
    return this.radius * 2;
  }
  
  /**
   * Get the ball's mesh
   */
  public getMesh(): THREE.Mesh {
    return this.mesh;
  }
  
  /**
   * Get the ball's position (returns the internal position object)
   * NOTE: Do not modify the returned vector as it's a reference!
   */
  public getPosition(): THREE.Vector3 {
    return this.position;
  }
  
  /**
   * Get a copy of the ball's position (creates a new object - use sparingly)
   */
  public getPositionCopy(): THREE.Vector3 {
    // Use reusable temp vector to avoid allocation
    this._tempVec3.copy(this.position);
    return this._tempVec3;
  }
  
  /**
   * Set the ball's position
   */
  public setPosition(x: number, y: number, z: number): void {
    this.position.set(x, y, z);
    this.mesh.position.copy(this.position);
  }
  
  /**
   * Set the ball's velocity (used for initial velocities when releasing the ball)
   */
  public setVelocity(x: number, y: number, z: number): void {
    this.velocity.set(x, y, z);
  }
  
  /**
   * Get the ball's velocity (returns the internal velocity object)
   * NOTE: Do not modify the returned vector as it's a reference!
   */
  public getVelocity(): THREE.Vector3 {
    return this.velocity;
  }
  
  /**
   * Get a copy of the ball's velocity (creates a new object - use sparingly)
   */
  public getVelocityCopy(): THREE.Vector3 {
    // Create a new vector to avoid modifying the internal state
    return new THREE.Vector3(this.velocity.x, this.velocity.y, this.velocity.z);
  }
} 